import { DateTime, Duration } from "luxon";
import { useState } from "react";
import { Constants } from "../../../../../../common/constants";
import { eventCreationStateToTemplateData } from "../../../../../../common/event_serialization_utils";
import {
	RoleOnRSVPAddTime,
	SerializedEvent,
	SeshThreadAutoArchiveDuration,
} from "../../../../../../common/sesh_types";
import { time_format_utils } from "../../../../../../common/time_format_utils";
import { trpc } from "../../../utils/trpc";
import { LoadingThrobber } from "../base_components/loading_throbber";
import { ChannelName } from "../channel_name";
import { useEventCreationContext } from "../hooks/use_event_creation_context";
import { useServerId } from "../hooks/use_server_id";
import { InlineEmoji } from "../inline_emoji";
import { rruleToRepeatString } from "../listings_form/event/event_listing_form/event_row";
import { MentionsList } from "../server_reminders/mentions_list";
import { EventTemplateModalSelectedStateRow } from "./event_template_modal_selected_state_row";
import { ColoredColor, TemplateSelection } from "./event_template_page";
import { EventTemplateSaveFavoriteInput } from "./event_template_save_favorite_input";

export function ExistingTemplateViewer(props: {
	selected_template: TemplateSelection;
}) {
	const server_id = useServerId();

	const event_query = trpc.event_template.get.useQuery(
		{
			guild_id: server_id,
			message_id: props.selected_template.message_id,
			template_id: props.selected_template.template_id,
		},
		{
			staleTime: Infinity,
		}
	);

	if (!event_query.isSuccess) {
		return <LoadingThrobber />;
	}

	return <TemplateViewer json={event_query.data.json}></TemplateViewer>;
}

export function StateTemplateViewer(props: {
	onClose: () => void;
	is_full?: boolean;
}) {
	const event_context = useEventCreationContext();

	return (
		<div>
			<TemplateViewer
				json={eventCreationStateToTemplateData(event_context.state)}
			></TemplateViewer>
			<div className="mt-4 flex flex-col gap-2">
				<EventTemplateSaveFavoriteInput
					initial_name={event_context.state.event_name}
					onSaveSuccess={props.onClose}
					is_full={props.is_full}
					save_props={{
						data: JSON.stringify(
							eventCreationStateToTemplateData(
								event_context.state
							)
						),
					}}
					label="Save"
				/>
			</div>
		</div>
	);
}

function TemplateViewer(props: {
	json: Partial<Omit<SerializedEvent, "event_message_id" | "author_id">>;
}) {
	const d = props.json;

	const start_time = d.start_time
		? DateTime.fromJSDate(new Date(d.start_time))
		: undefined;
	const end_time = d.duration_ms
		? DateTime.fromJSDate(new Date(d.duration_ms))
		: undefined;

	const emojis = d.rsvp_options.custom_options.map((val, idx) => (
		<InlineEmoji key={idx} option={val} />
	));

	return (
		<div className="flex h-[384px] flex-col gap-1 overflow-auto border border-base-dark bg-base-semidark p-4">
			<EventTemplateModalSelectedStateRow
				label="Name"
				value={d.event_name}
			/>
			<EventTemplateModalSelectedStateRow
				label="Start Time"
				value={
					start_time
						? time_format_utils.format_time_verbose_date_first(
								start_time
						  )
						: undefined
				}
			/>
			<EventTemplateModalSelectedStateRow
				label="Duration"
				value={
					d.duration_ms
						? time_format_utils.formatDurationVerbose(
								Duration.fromMillis(d.duration_ms)
						  )
						: undefined
				}
			/>
			<EventTemplateModalSelectedStateRow
				label="Description"
				value={d.description ? d.description : undefined}
			/>
			<EventTemplateModalSelectedStateRow
				label="Channel"
				value={<ChannelName channel_id={d.sent_channel_data.id} />}
			/>
			<EventTemplateModalSelectedStateRow
				label="Repeat"
				childs={
					d.rrule_str
						? [
								{
									label: "Interval",
									value: d.rrule_str
										? rruleToRepeatString(
												d.rrule_str,
												start_time ?? DateTime.now()
										  )
										: "None",
								},
								{
									label: "Allow Maintain RSVP",
									value: d.allow_maintain_repeat_rsvp.toString(),
								},
								{
									label: "Repeat Timezone",
									value: d.event_timezone,
								},
						  ]
						: [{ label: "Enabled", value: "false" }]
				}
			/>
			<EventTemplateModalSelectedStateRow
				label="RSVP Options"
				value={
					emojis.length > 0 ? (
						<div className="flex gap-1">{emojis}</div>
					) : undefined
				}
			/>
			<EventTemplateModalSelectedStateRow
				label="Color"
				value={
					<ColoredColor value={d.color ?? Constants.EMBED_COLOR} />
				}
			/>
			<EventTemplateModalSelectedStateRow
				label="Mentions on Create"
				value={
					<div className="flex gap-1">
						<MentionsList
							role_mention_ids={d.role_mentions_on_create}
							user_mention_ids={d.user_mentions_on_create}
						/>
					</div>
				}
			/>
			<EventTemplateModalSelectedStateRow
				label="Role Restrictions"
				value={
					<div className="flex gap-1">
						<MentionsList
							role_mention_ids={d.eligible_role_ids}
							user_mention_ids={[]}
						/>
					</div>
				}
			/>
			<EventTemplateModalSelectedStateRow
				label="Hide Attendees"
				value={d.hide_attendees.toString()}
			/>
			<EventTemplateModalSelectedStateRow
				label="Image URL"
				value={d.image_url}
			/>
			<EventTemplateModalSelectedStateRow
				label="Attendee Roles"
				childs={[
					{
						label: "Roles",
						value: (
							<div className="flex gap-1">
								<MentionsList
									role_mention_ids={
										d.attendee_role_data.role_ids
									}
									user_mention_ids={[]}
								/>
							</div>
						),
					},
					{
						label: "Role Add Time",
						value:
							d.attendee_role_data.role_add_time ===
							RoleOnRSVPAddTime.OnRSVP
								? "RSVP"
								: "Event Start",
					},
					{
						label: "Remove on End",
						value: d.attendee_role_data.remove_on_end.toString(),
					},
				]}
			/>
			<EventTemplateModalSelectedStateRow
				label="Thread Settings"
				childs={[
					{
						label: "Enabled",
						value: Boolean(d.thread_settings).toString(),
					},
					d.thread_settings
						? {
								label: "Thread Title",
								value: d.thread_settings.name,
						  }
						: undefined,
					d.thread_settings
						? {
								label: "Auto Archive Duration",
								value: SeshThreadAutoArchiveDuration[
									d.thread_settings.auto_archive_duration
								],
						  }
						: undefined,
					d.thread_settings
						? {
								label: "Join on RSVP",
								value: d.thread_settings.should_auto_join_attendees_on_rsvp.toString(),
						  }
						: undefined,
					d.thread_settings
						? {
								label: "Start Message",
								value: d.thread_settings
									.should_send_start_message_to_thread
									? "Thread"
									: "Channel",
						  }
						: undefined,
				].filter((val) => val !== undefined)}
			/>
			<EventTemplateModalSelectedStateRow
				label="Notifications"
				childs={d.server_reminders.map((val) => ({
					label:
						time_format_utils.formatDurationVerbose(
							Duration.fromMillis(val.fire_before_ms)
						) + " before",
					childs: [
						{
							label: "Channel",
							value: (
								<ChannelName
									channel_id={
										val.desired_channel_id ??
										d.sent_channel_data.id
									}
								/>
							),
						},
						{
							label: "Mentions",
							value: (
								<div className="flex gap-1">
									<MentionsList
										role_mention_ids={val.role_mention_ids}
										user_mention_ids={val.user_mention_ids}
									/>
								</div>
							),
						},
						{
							label: "Title",
							value: val.message_title,
						},
					],
				}))}
			/>
			<EventTemplateModalSelectedStateRow
				label="RSVP Lock Before Time"
				value={
					d.rsvp_lock_before_ms
						? Duration.fromMillis(d.rsvp_lock_before_ms).toFormat(
								"d 'days,' h 'hours,' m 'minutes'"
						  )
						: undefined
				}
			/>
		</div>
	);
}
